// OPFS tile store — stores the Maine .pmtiles archive in the browser's
// Origin Private File System so the map works 100% offline.
// Bluefly owns: download orchestration, storage quota management.
// PMTiles owns: the archive format and byte-range read protocol.

export const MAINE_PMTILES_URL =
  'https://r2.openfreemap.org/planet/20250109_041_2hGBE6/tiles.pmtiles';

// We use a small Maine extract served from our own Cloudflare R2 bucket.
// Fallback: Protomaps public extract (online only).
export const MAINE_TILE_KEY = 'maine-v1.pmtiles';

// ─── OPFS helpers ─────────────────────────────────────────────────────────────

export async function getOPFSRoot(): Promise<FileSystemDirectoryHandle> {
  return navigator.storage.getDirectory();
}

export async function hasTileFile(key: string): Promise<boolean> {
  try {
    const root = await getOPFSRoot();
    await root.getFileHandle(key, { create: false });
    return true;
  } catch {
    return false;
  }
}

export async function getTileFileSize(key: string): Promise<number> {
  try {
    const root = await getOPFSRoot();
    const handle = await root.getFileHandle(key);
    const file = await handle.getFile();
    return file.size;
  } catch {
    return 0;
  }
}

export async function deleteTileFile(key: string): Promise<void> {
  const root = await getOPFSRoot();
  await root.removeEntry(key);
}

/**
 * Download the PMTiles archive to OPFS, streaming the response so we can
 * report progress without loading the full file into RAM.
 */
export async function downloadTilesToOPFS(
  url: string,
  key: string,
  onProgress: (pct: number, bytesDownloaded: number, totalBytes: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const root = await getOPFSRoot();
  const fileHandle = await root.getFileHandle(key, { create: true });
  const writable = await fileHandle.createWritable();

  try {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    if (!response.body) throw new Error('No response body');

    const contentLength = Number(response.headers.get('content-length') ?? '0');
    const reader = response.body.getReader();
    let downloaded = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      await writable.write(value);
      downloaded += value.byteLength;
      const pct = contentLength > 0 ? Math.round((downloaded / contentLength) * 100) : 0;
      onProgress(pct, downloaded, contentLength);
    }

    await writable.close();
  } catch (err) {
    await writable.abort();
    // Clean up partial file
    try { await root.removeEntry(key); } catch { /* ignore */ }
    throw err;
  }
}

/**
 * Create a File object from the OPFS tile file so the PMTiles JS client
 * can read byte ranges from it directly without loading into RAM.
 */
export async function getTileFile(key: string): Promise<File> {
  const root = await getOPFSRoot();
  const handle = await root.getFileHandle(key);
  return handle.getFile();
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}
